<template>
  <div class="gs-app gs-analytics">
    <gs-card class="gs-analytics__header">
      <div class="gs-analytics__intro">
        <span class="gs-analytics__eyebrow">Performance</span>
        <h1 class="gs-analytics__title">See what earns attention</h1>
        <p class="gs-analytics__subtitle">
          Compare player loads, unique viewers and plays across your library, then open any video for
          its own story.
        </p>
      </div>

      <date-range-pill :preset="preset" :custom="custom" @change="onRangeChange" />
    </gs-card>

    <analytics-error-card v-if="failed" @retry="load()" />

    <template v-else>
      <report-status :report="report" :loading="chartLoading" />
      <div class="gs-analytics__stats">
        <stat-card
          v-for="card in cards"
          :key="card.label"
          :label="card.label"
          :value="chartLoading ? '—' : card.value"
          :note="card.note"
          :icon="card.icon"
          :tone="card.tone"
        />
      </div>

      <traffic-chart
        :series="chartValues"
        :metric="metric"
        :loading="chartLoading"
        @update:metric="onMetricChange"
      />

      <gs-card class="gs-analytics__table-card">
        <div class="gs-analytics__table-head">
          <h2 class="gs-analytics__table-title">Video performance</h2>
          <gs-button variant="outline" :disabled="tableLoading || !rows.length" @click="exportPage">Export this page</gs-button>
          <p class="gs-analytics__table-subtitle">Videos matching your filters. Metrics cover {{ rangeLabel }}; videos without activity show zero.</p>
        </div>

        <div class="gs-analytics__filters">
          <gs-field class="gs-analytics__search-field">
            <div class="gs-analytics__search">
              <gs-icon name="search" size="15" class="gs-analytics__search-icon" />
              <input
                type="search"
                class="gs-analytics__search-input"
                :value="query"
                placeholder="Search videos"
                aria-label="Search videos"
                @input="onSearchInput($event.target.value)"
              />
            </div>
          </gs-field>

          <treeselect
            class="gs-analytics__category"
            :value="category"
            :multiple="false"
            :options="categoryList"
            :normalizer="normalizer"
            :close-on-select="true"
            :clearable="false"
            :clear-on-select="true"
            :is-default-expanded="true"
            :default-expand-level="1"
            :show-count="true"
            :max-height="280"
            placeholder="All categories"
            @input="onCategoryChange"
          />

          <div class="gs-analytics__segmented" role="group" aria-label="Show data as">
            <button
              type="button"
              data-format="numbers"
              :class="['gs-analytics__segment', { 'is-active': !percent }]"
              :aria-pressed="!percent ? 'true' : 'false'"
              @click="percent = false"
            >
              Numbers
            </button>
            <button
              type="button"
              data-format="percentages"
              :class="['gs-analytics__segment', { 'is-active': percent }]"
              :aria-pressed="percent ? 'true' : 'false'"
              @click="percent = true"
            >
              Percentages
            </button>
          </div>
        </div>

        <video-performance-table
          :rows="rows"
          :total="total"
          :page="page"
          :limit="limit"
          :order-by="orderBy"
          :ascending="ascending"
          :percent="percent"
          :loading="tableLoading"
          @sort="onSort"
          @update:page="onPageChange"
          @update:limit="onLimitChange"
          @open="openVideo"
        />

        <div class="gs-analytics__note">
          <analytics-footnote />
        </div>
      </gs-card>
    </template>
  </div>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import Category from '@/models/Category';
import { buildRange } from '@/utils/analyticsRange';
import {
  fetchChart,
  downloadCsv,
  fetchTable,
  summaryFrom,
  latestOnly,
  formatNumber,
  buildStatCards,
} from '@/services/analyticsApi';
import ReportStatus from '@/components/analytics/ReportStatus.vue';
import StatCard from '@/components/analytics/StatCard.vue';
import TrafficChart from '@/components/analytics/TrafficChart.vue';
import DateRangePill from '@/components/analytics/DateRangePill.vue';
import VideoPerformanceTable from '@/components/analytics/VideoPerformanceTable.vue';
import AnalyticsErrorCard from '@/components/analytics/AnalyticsErrorCard.vue';
import AnalyticsFootnote from '@/components/analytics/AnalyticsFootnote.vue';

export default {
  name: 'AnalyticsLibrary',
  middleware: 'auth',
  layout: 'default',

  components: {
    ReportStatus,
    Treeselect,
    StatCard,
    TrafficChart,
    DateRangePill,
    VideoPerformanceTable,
    AnalyticsErrorCard,
    AnalyticsFootnote,
  },

  metaInfo() {
    return { title: 'Video Analytics' };
  },

  data() {
    return {
      preset: 'last30',
      custom: null,
      metric: 'plays',
      category: 0,
      query: '',
      orderBy: 'id',
      ascending: false,
      limit: 10,
      page: 1,
      percent: false,
      summary: summaryFrom(),
      report: {},
      chartValues: [],
      rows: [],
      total: 0,
      chartLoading: true,
      tableLoading: true,
      failed: false,
      categoryList: [{ id: 0, category: 'All categories', user_id: 0 }],
      searchTimer: null,
    };
  },

  computed: {
    range() {
      return buildRange(this.preset, this.custom);
    },
    rangeLabel() {
      return this.range.label;
    },
    cards() {
      return buildStatCards(this.summary, formatNumber);
    },
  },

  created() {
    // Non-reactive stale-response guards: one sequence per loader, so a slow
    // earlier request can never overwrite a faster later one.
    this.chartGuard = latestOnly();
    this.tableGuard = latestOnly();
    this.loadCategories();
    this.load();
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },

  methods: {
    normalizer(node) {
      return {
        id: node.id,
        label: node.category,
        children: node.child && node.child.length ? node.child : undefined,
        user_id: node.user_id,
      };
    },

    async loadCategories() {
      try {
        const categories = await Category.$get();
        this.categoryList = [{ id: 0, category: 'All categories', user_id: 0 }].concat(categories || []);
      } catch (e) {
        // The report itself still works without the category filter.
      }
    },

    load() {
      this.failed = false;
      return Promise.all([this.loadChart(), this.loadTable()]);
    },

    async loadChart() {
      const done = this.chartGuard();
      this.chartLoading = true;
      try {
        const data = await fetchChart({ metric: 'all', range: this.range, category: this.category, query: this.query });
        if (!done()) return;
        this.report = data || {};
        this.chartValues = (this.report.series && this.report.series[this.metric]) || [];
        this.summary = summaryFrom(data && data.selected_tab_data);
      } catch (e) {
        if (!done()) return;
        this.failed = true;
      } finally {
        if (done()) this.chartLoading = false;
      }
    },

    async loadTable() {
      const done = this.tableGuard();
      this.tableLoading = true;
      try {
        const { rows, total } = await fetchTable({
          range: this.range,
          category: this.category,
          query: this.query,
          orderBy: this.orderBy,
          ascending: this.ascending,
          limit: this.limit,
          page: this.page,
        });
        if (!done()) return;
        this.rows = rows || [];
        this.total = total || 0;
      } catch (e) {
        if (!done()) return;
        this.failed = true;
      } finally {
        if (done()) this.tableLoading = false;
      }
    },

    onRangeChange({ preset, custom }) {
      this.preset = preset;
      this.custom = custom;
      this.page = 1;
      this.load();
    },

    onMetricChange(metric) {
      if (metric === this.metric) return;
      this.metric = metric;
      this.chartValues = (this.report.series && this.report.series[metric]) || [];
    },

    onCategoryChange(value) {
      this.category = typeof value === 'undefined' ? 0 : value;
      this.page = 1;
      this.load();
    },

    onSearchInput(value) {
      this.query = value;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.load();
      }, 300);
    },

    onSort(column) {
      if (this.orderBy === column) {
        this.ascending = !this.ascending;
      } else {
        this.orderBy = column;
        this.ascending = false;
      }
      this.page = 1;
      this.loadTable();
    },

    onPageChange(page) {
      this.page = page;
      this.loadTable();
    },

    onLimitChange(limit) {
      this.limit = limit;
      this.page = 1;
      this.loadTable();
    },

    exportPage() {
      downloadCsv('groovevideo-performance-page-' + this.page + '.csv', [['Video', 'Player loads', 'Daily unique viewers', 'Plays', 'Finishes', 'Watch time'], ...this.rows.map(row => [row.name, row.impressions, row.uniques, row.plays, row.finishes, row.total_time_watched])]);
    },
    openVideo(id) {
      this.$router.push({ name: 'videoAnalytics', params: { videoId: String(id) } });
    },
  },
};
</script>

<style scoped>
.gs-analytics {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.gs-analytics__header {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 28px;
}

.gs-analytics__intro {
  min-width: 0;
}

.gs-analytics__eyebrow {
  display: block;
  margin-bottom: 6px;
  color: var(--gs-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-analytics__title {
  margin: 0 0 6px;
  color: var(--gs-foreground);
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}

.gs-analytics__subtitle {
  margin: 0;
  max-width: 62ch;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.55;
}

.gs-analytics__stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 16px;
}

.gs-analytics__table-card {
  padding: 20px 24px 18px;
}

.gs-analytics__table-head {
  margin-bottom: 16px;
}

.gs-analytics__table-title {
  margin: 0 0 4px;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
}

.gs-analytics__table-subtitle {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
}

.gs-analytics__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
}

.gs-analytics__search-field {
  flex: 1 1 240px;
  min-width: 220px;
}

.gs-analytics__search {
  display: flex;
  gap: 8px;
  align-items: center;
  min-height: 40px;
  padding: 0 12px;
  background: var(--gs-input);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-analytics__search:focus-within {
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-analytics__search-icon {
  flex: none;
  color: var(--gs-muted-foreground);
}

.gs-analytics__search-input {
  flex: 1 1 auto;
  min-width: 0;
  color: var(--gs-foreground);
  background: transparent;
  border: 0;
  font-size: 14px;
}

.gs-analytics__search-input:focus {
  outline: none;
}

.gs-analytics__category {
  flex: none;
  width: 220px;
}

.gs-analytics__category ::v-deep .vue-treeselect__control {
  min-height: 40px;
  border-radius: var(--gs-radius-sm);
}

.gs-analytics__segmented {
  display: inline-flex;
  padding: 3px;
  background: var(--gs-muted);
  border-radius: var(--gs-radius-md);
}

.gs-analytics__segment {
  padding: 6px 12px;
  color: var(--gs-muted-foreground);
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.gs-analytics__segment:hover {
  color: var(--gs-foreground);
}

.gs-analytics__segment:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-analytics__segment.is-active {
  color: var(--gs-foreground);
  background: var(--gs-surface);
  box-shadow: var(--gs-shadow-sm);
}

.gs-analytics__note {
  margin-top: 16px;
}

@media (max-width: 640px) {
  .gs-analytics__header,
  .gs-analytics__table-card {
    padding: 18px 18px 16px;
  }

  .gs-analytics__filters > * {
    width: 100%;
  }

  .gs-analytics__category {
    width: 100%;
  }
}
</style>
